import { useDispatch, useSelector } from "react-redux";

import useAbortableEffect from "app/pages/sales-intelligence/hooks/useAbortableEffect";
import { createContactsRequestDto } from "app/pages/sales-intelligence/modules/company-analysis/helpers/contacts";
import { useSelectedAvailableCountries } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useSelectedAvailableCountries";
import {
    selectActiveProfile,
    selectContactDetails,
    selectContactsCurrentPage,
    selectContactsPageSize,
    selectContactsTableSearchText,
    selectContactsTableSortBy,
} from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/selectors";
import useRevealQuotaFetchEffect from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useRevealQuotaFetchEffect";
import { contactsSearchResultsWithTotalCountFetchedAsync } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/action-creators";
import { fetchContactsSearchResultsWithTotalCountThunk } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import {
    selectActionsPanelFilters,
    selectQuotaIsBeingFetched,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import { selectIsContactsVisible } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";

type FetchActionsReturnType = ReturnType<
    | typeof contactsSearchResultsWithTotalCountFetchedAsync.success
    | typeof contactsSearchResultsWithTotalCountFetchedAsync.failure
>;

type FetchReturnType = Promise<FetchActionsReturnType | undefined>;

type ContactsFetchEffectResult = (signal?: AbortSignal) => FetchReturnType;

export default function useContactsFetchEffect(domain: string): ContactsFetchEffectResult {
    const dispatch = useDispatch();
    // Quota info
    const isQuotaBeingFetched = useSelector(selectQuotaIsBeingFetched);
    // Contacts countries
    const { selectedCountriesIds, isCountriesTransitioning } = useSelectedAvailableCountries();

    // Contacts profiles
    const selectedProfile = useSelector(selectActiveProfile);
    // Contacts details
    const contactDetails = useSelector(selectContactDetails);
    // Contacts search table
    const searchText = useSelector(selectContactsTableSearchText);
    const currentPage = useSelector(selectContactsCurrentPage);
    const pageSize = useSelector(selectContactsPageSize);
    // Contacts actions panel
    const actionsPanelFilters = useSelector(selectActionsPanelFilters);
    const sortBy = useSelector(selectContactsTableSortBy);

    // Access info
    const isContactsVisible = useSelector(selectIsContactsVisible);

    const requestDTO = createContactsRequestDto({
        domain,
        searchText,
        currentPage,
        pageSize,
        contactDetails,
        selectedProfile,
        selectedCountriesIds,
        actionsPanelFilters,
        sortBy,
    });

    // Quota fetch
    useRevealQuotaFetchEffect();

    const fetchData = (signal?: AbortSignal): FetchReturnType => {
        return dispatch(fetchContactsSearchResultsWithTotalCountThunk(requestDTO, { cancellation: signal }));
    };

    useAbortableEffect(
        (signal) => {
            if (isContactsVisible && !isQuotaBeingFetched && !isCountriesTransitioning) {
                void fetchData(signal);
            } else if (isCountriesTransitioning) {
                dispatch(contactsSearchResultsWithTotalCountFetchedAsync.request());
            }
        },
        [
            currentPage,
            selectedCountriesIds,
            searchText,
            selectedProfile,
            contactDetails,
            isQuotaBeingFetched,
            isContactsVisible,
            isCountriesTransitioning,
            sortBy,
        ],
    );

    return fetchData;
}
